import { colors, getWidth } from '@cabonline/ui-shared';
import React, { FC, ReactNode, useId } from 'react';

import { Text } from '../../text';
import { Modal } from '../modal';
import type { ModalProps } from '../types';
import { StyledActions, StyledContent, StyledIconWrap, StyledTitleWrapper } from './styles';

export interface FeedbackModalProps {
  isOpen?: boolean;
  title: ModalProps['title'];
  text?: ReactNode;
  icon?: React.JSX.Element;
  actions?: React.JSX.Element;
  showCloseButton?: ModalProps['showCloseButton'];
  closeButtonLabel?: ModalProps['closeButtonLabel'];
  onClose?: ModalProps['onClose'];
  isSecondaryModal?: boolean;
  isDesktop?: boolean;
  role?: string;
}

export const FeedbackModal: FC<FeedbackModalProps> = ({
  isOpen = true,
  title,
  text,
  icon,
  actions = null,
  onClose,
  showCloseButton,
  closeButtonLabel,
  isSecondaryModal = false,
  isDesktop = true,
  role = 'alertdialog',
}) => {
  const titleId = useId();
  const descriptionId = useId();

  const resolvedShowCloseButton = showCloseButton ?? !!onClose;

  return (
    <Modal
      role={role}
      isOpen={isOpen}
      title={title}
      hideTitle
      showCloseButton={resolvedShowCloseButton}
      closeButtonLabel={closeButtonLabel}
      width={{ desktop: getWidth('modal') }}
      onClose={onClose}
      isSecondaryModal={isSecondaryModal}
      labelledBy={titleId}
      describedBy={text != null ? descriptionId : undefined}
    >
      <StyledContent hasCloseButton={resolvedShowCloseButton}>
        {icon && <StyledIconWrap isDesktop={isDesktop}>{icon}</StyledIconWrap>}
        <StyledTitleWrapper>
          <Text
            id={titleId}
            size="xl"
            weight="extraBold"
            marginBottom="xs"
            as="h2"
            testID="feedback-modal-title"
          >
            {title}
          </Text>
        </StyledTitleWrapper>
        {text != null && typeof text === 'string' ? (
          <Text id={descriptionId} size={isDesktop ? 'l' : 'm'} color={colors.gray1}>
            {text}
          </Text>
        ) : text != null ? (
          <div id={descriptionId}>{text}</div>
        ) : null}
        {actions && <StyledActions>{actions}</StyledActions>}
      </StyledContent>
    </Modal>
  );
};
